import { combineReducers } from 'redux'
import { createSelector } from 'reselect'

import { FETCH_ENERGY_DISTRIBUTION, FETCH_ENERGY_HISTORY, FETCH_NUTRITION_SUMMARY } from './actionTypes'
import { createPendingCounterReducer } from './common'
import {
  analyticsCacheReducer,
  fetchAnalyticsWithCache,
  getDisplayedAnalyticsCacheEntry,
  hasActiveAnalyticsCacheEntry
} from './myAnalyticsCache'

import {
  DateRange,
  EnergyDistributionType,
  EnergyHistoryItem,
  Macronutrients,
  MyAnalyticsReduxState,
  NutritionSummary,
  PendingCounterMultipleState,
  ReduxStoreState,
  ThunkAction
} from 'Models'
import { coreBackendService } from 'Services'
import { Calculator } from 'Utils'

const sumObject = (obj?: Macronutrients): number => Object.values(obj ?? {}).reduce((acc, val) => acc + (val ?? 0), 0)

export { ANALYTICS_CACHE_MAX_AGE_MS, ANALYTICS_CACHE_MAX_ENTRIES, initialCache } from './myAnalyticsCache'

export const fetchNutritionSummaryAction = (range: DateRange): ThunkAction =>
  fetchAnalyticsWithCache(range, 'nutritionSummary', FETCH_NUTRITION_SUMMARY, () =>
    coreBackendService.fetchNutritionSummary(range)
  )

export const fetchEnergyHistoryAction = (range: DateRange): ThunkAction =>
  fetchAnalyticsWithCache(range, 'energyHistory', FETCH_ENERGY_HISTORY, () =>
    coreBackendService.fetchEnergyHistory(range)
  )

export const fetchEnergyDistributionAction = (range: DateRange): ThunkAction =>
  fetchAnalyticsWithCache(range, 'energyDistribution', FETCH_ENERGY_DISTRIBUTION, () =>
    coreBackendService.fetchEnergyMealDistribution(range)
  )

const toDisplayNutritionSummary = (payload?: NutritionSummary): NutritionSummary => {
  if (!payload) {
    return {}
  }

  const summaryAccepted = sumObject(payload.accepted?.amount)
  const summaryGoal = sumObject(payload.goal?.amount)

  return {
    ...(summaryAccepted ? { accepted: payload.accepted } : {}),
    ...(summaryGoal ? { goal: payload.goal } : {})
  }
}

const toDisplayEnergyHistory = (payload?: EnergyHistoryItem[]): EnergyHistoryItem[] => {
  if (!payload?.some((item) => item.acceptedEnergy > 0 && item.goalEnergy > 0)) {
    return []
  }

  return payload
}

const toDisplayEnergyDistribution = (payload?: EnergyDistributionType): EnergyDistributionType | undefined => {
  if (!payload) {
    return undefined
  }

  const energyTotal = Object.values<number>(payload).reduce((acc, val) => acc + val, 0)

  return energyTotal > 0
    ? Object.entries(payload).reduce<EnergyDistributionType>(
        (acc, [key, value]) => ({
          ...acc,
          [key]: Calculator.calculatePercentage(value, energyTotal)
        }),
        {} as EnergyDistributionType
      )
    : undefined
}

export const myAnalyticsSelector = ({ myAnalytics }: ReduxStoreState): MyAnalyticsReduxState => myAnalytics

const analyticsCacheSelector = ({ myAnalytics }: ReduxStoreState): MyAnalyticsReduxState['cache'] => myAnalytics.cache

export const analyticsCacheRevisionSelector = ({ myAnalytics }: ReduxStoreState): number => myAnalytics.cache.revision

export const isNutritionSummaryLoadingSelector = ({ myAnalytics }: ReduxStoreState): boolean =>
  !!myAnalytics.pendingCounter.pendingCounterNutritionSummary &&
  !hasActiveAnalyticsCacheEntry(myAnalytics.cache, 'nutritionSummary')

export const isEnergyHistoryLoadingSelector = ({ myAnalytics }: ReduxStoreState): boolean =>
  !!myAnalytics.pendingCounter.pendingCounterEnergyHistory &&
  !hasActiveAnalyticsCacheEntry(myAnalytics.cache, 'energyHistory')

export const isEnergyDistributionLoadingSelector = ({ myAnalytics }: ReduxStoreState): boolean =>
  !!myAnalytics.pendingCounter.pendingCounterEnergyDistribution &&
  !hasActiveAnalyticsCacheEntry(myAnalytics.cache, 'energyDistribution')

export const nutritionSummarySelector = createSelector([analyticsCacheSelector], (cache) =>
  toDisplayNutritionSummary(getDisplayedAnalyticsCacheEntry(cache, 'nutritionSummary')?.data)
)
export const nutritionSummaryRecommendedSelector = createSelector([nutritionSummarySelector], (data) => data.goal)
export const nutritionSummaryTrackedSelector = createSelector([nutritionSummarySelector], (data) => data.accepted)

export const energyHistorySelector = createSelector([analyticsCacheSelector], (cache) =>
  toDisplayEnergyHistory(getDisplayedAnalyticsCacheEntry(cache, 'energyHistory')?.data)
)

export const energyDistributionSelector = createSelector([analyticsCacheSelector], (cache) =>
  toDisplayEnergyDistribution(getDisplayedAnalyticsCacheEntry(cache, 'energyDistribution')?.data)
)

export default combineReducers<MyAnalyticsReduxState>({
  cache: analyticsCacheReducer,
  pendingCounter: combineReducers<PendingCounterMultipleState>({
    pendingCounterNutritionSummary: createPendingCounterReducer(FETCH_NUTRITION_SUMMARY),
    pendingCounterEnergyHistory: createPendingCounterReducer(FETCH_ENERGY_HISTORY),
    pendingCounterEnergyDistribution: createPendingCounterReducer(FETCH_ENERGY_DISTRIBUTION)
  })
})
